import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

// Shared product button system. Keep variants stable because feature pages
// rely on them for existing actions.
const buttonVariants = cva(
  // Poppins sits tall in its line box. `leading-tight` gives its ascenders enough
  // headroom that the tops of glyphs aren't shaved inside fixed-height buttons
  // (line-height:1 / leading-none left only ~0.75px cap clearance that rounds
  // away at devicePixelRatio 1 — non-retina / zoom). It MUST live on each size
  // variant AFTER its `text-*`, NOT here: tailwind-merge treats font-size +
  // line-height as one group, so a later `text-*` would strip a base leading.
  "inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium transition-[background-color,color,border-color,box-shadow,transform] duration-200 ease-smooth focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30 focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      // A disabled FILLED button at plain opacity-50 still reads as a solid,
      // clickable pill — the audit caught a greyed-but-solid "Save" outshouting
      // the live actions beside it. Filled variants therefore drop their fill
      // and shadow when disabled so "inert" is legible at a glance. Variants
      // with no fill to mislead with (outline/ghost/link) keep the plain fade.
      variant: {
        default:
          "border border-transparent bg-primary text-primary-foreground shadow-sm hover:bg-primary-hover hover:shadow-md active:translate-y-0 disabled:border-[#ececec] dark:border-[#ffffff1a] disabled:bg-muted disabled:text-muted-foreground disabled:shadow-none",
        destructive:
          "border border-destructive/20 bg-destructive text-destructive-foreground shadow-control hover:bg-destructive/90 hover:shadow-none active:translate-y-0 disabled:border-[#ececec] dark:border-[#ffffff1a] disabled:bg-muted disabled:text-muted-foreground disabled:shadow-none",
        secondary:
          "border border-[#ececec] dark:border-[#ffffff1a] bg-secondary text-secondary-foreground shadow-control hover:border-[#ececec] dark:border-[#ffffff1a] hover:bg-secondary/80 disabled:text-muted-foreground disabled:shadow-none disabled:opacity-60",
        soft:
          "border border-accent/25 bg-accent/20 text-accent-foreground shadow-control hover:bg-accent/30 disabled:border-[#ececec] dark:border-[#ffffff1a] disabled:bg-muted disabled:text-muted-foreground disabled:shadow-none",
        outline:
          "border border-input bg-card shadow-control hover:border-ring/40 hover:bg-secondary hover:text-foreground disabled:text-muted-foreground disabled:shadow-none disabled:opacity-60",
        ghost: "hover:bg-secondary hover:text-foreground disabled:opacity-40",
        gold:
          "border border-brand-gold/20 bg-brand-gold text-brand-gold-foreground shadow-control hover:bg-brand-gold/90 hover:shadow-none active:translate-y-0 disabled:border-[#ececec] dark:border-[#ffffff1a] disabled:bg-muted disabled:text-muted-foreground disabled:shadow-none",
        link: "text-primary underline-offset-4 hover:underline disabled:opacity-50",
      },
      size: {
        xs: "h-7 rounded-full px-2.5 text-xs leading-tight",
        sm: "h-8 rounded-full px-3 text-[13.5px] leading-tight",
        default: "h-9 rounded-full px-3.5 text-sm leading-tight",
        lg: "h-10 rounded-full px-5 text-sm leading-tight",
        xl: "h-11 rounded-full px-6 text-[15px] leading-tight",
        iconSm: "h-8 w-8 rounded-full",
        icon: "h-9 w-9 rounded-full",
        iconLg: "h-10 w-10 rounded-full",
        touch: "min-h-[44px] rounded-full px-4 text-sm leading-tight",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
